import { useI18n } from './i18n'
import type { MessageKey } from './i18n'

export type BriefEvidence = { section: string; path: string; as_of: string }
export type BriefBriefing = {
  summary: string
  observations: Array<{ id: string; text: string; evidence: BriefEvidence[] }>
  watches: Array<{ id: string; trigger: string; condition: string; observe: BriefEvidence[]; invalidation: string }>
}
export type BriefCoverage = {
  available: number
  expected: number
  missing: string[]
  freshness: Array<{ section: string; as_of: string; state: 'current' | 'prior' | 'unknown' }>
}

const SECTION_LABEL_KEYS: Record<string, MessageKey> = {
  close_recap: 'brief.section.closeRecap',
  movers: 'brief.section.movers', key_levels: 'brief.section.keyLevels',
  option_flow: 'brief.section.optionFlow', earnings_recap: 'brief.section.earningsRecap',
  tomorrow_watch: 'brief.section.tomorrowWatch', macro_watch: 'brief.section.macroWatch',
  today_earnings: 'brief.section.todayEarnings', earnings_iv_watch: 'brief.section.earningsIvWatch',
  uoa_morning: 'brief.section.uoaMorning', midday_market: 'brief.section.middayMarket',
}

export function briefSectionAnchor(section: string): string {
  return `brief-section-${section.replace(/[^a-z0-9_-]/gi, '-')}`
}

export function BriefExecutiveLayer({ briefing, coverage }: { briefing?: BriefBriefing; coverage?: BriefCoverage }) {
  const { t } = useI18n()
  const observations = briefing?.observations?.slice(0, 3) || []
  const watches = briefing?.watches?.slice(0, 3) || []
  const hasBriefing = Boolean(briefing?.summary || observations.length || watches.length)
  const hasCoverage = Boolean(coverage && (coverage.expected > 0 || coverage.missing?.length || coverage.freshness?.length))
  if (!hasBriefing && !hasCoverage) return null

  const sectionLabel = (section: string) => {
    const key = SECTION_LABEL_KEYS[section]
    return key ? t(key) : t('brief.section.other')
  }
  const evidenceLinks = (items: BriefEvidence[] | undefined) => (items || []).map((item, index) => (
    <a className="brief-evidence-link" href={`#${briefSectionAnchor(item.section)}`} key={`${item.section}-${item.as_of}-${index}`}>
      <span>{sectionLabel(item.section)}</span>{item.as_of && <time>{item.as_of}</time>}
    </a>
  ))

  return (
    <section className="brief-executive" aria-label={t('brief.executive.title')}>
      {hasBriefing && <>
        <div className="brief-executive__intro">
          <p className="kicker" id="brief-executive-title">{t('brief.executive.title')}</p>
          {briefing?.summary && <p className="brief-executive__summary">{briefing.summary}</p>}
        </div>
        {observations.length > 0 && <div className="brief-executive__group">
          <h2>{t('brief.executive.observations')}</h2>
          <div className="brief-observation-list">{observations.map((observation, index) => (
            <article className="brief-observation" key={observation.id || index}>
              <span className="brief-card-index">{String(index + 1).padStart(2, '0')}</span>
              <div><p>{observation.text}</p>{observation.evidence?.length > 0 && <details className="brief-evidence">
                <summary>{t('brief.executive.evidence')}</summary><div className="brief-evidence-links">{evidenceLinks(observation.evidence)}</div>
              </details>}</div>
            </article>
          ))}</div>
        </div>}
        {watches.length > 0 && <div className="brief-executive__group">
          <h2>{t('brief.executive.watches')}</h2>
          <div className="brief-watch-grid">{watches.map((watch, index) => (
            <article className="brief-watch" key={watch.id || index}>
              <p className="brief-watch__trigger">{watch.trigger}</p>
              <dl><div><dt>{t('brief.executive.condition')}</dt><dd>{watch.condition}</dd></div>
                {watch.invalidation && <div><dt>{t('brief.executive.recheck')}</dt><dd>{watch.invalidation}</dd></div>}</dl>
              {watch.observe?.length > 0 && <details className="brief-evidence"><summary>{t('brief.executive.observe')}</summary>
                <div className="brief-evidence-links">{evidenceLinks(watch.observe)}</div></details>}
            </article>
          ))}</div>
        </div>}
      </>}
      {hasCoverage && coverage && <details className="brief-coverage">
        <summary><span>{t('brief.coverage.title')}</span><strong>{coverage.available}/{coverage.expected}</strong><span className="muted">{t('brief.coverage.available')}</span></summary>
        <p className="brief-coverage__note">{t('brief.coverage.note')}</p>
        {coverage.missing?.length > 0 && <p className="brief-coverage__missing"><strong>{t('brief.coverage.missing')}</strong>{' '}{coverage.missing.map(sectionLabel).join(t('brief.coverage.separator'))}</p>}
        {coverage.freshness?.length > 0 && <div className="brief-freshness"><p>{t('brief.coverage.freshness')}</p><ul>
          {coverage.freshness.map((item, index) => <li key={`${item.section}-${index}`}><span>{sectionLabel(item.section)}</span>
            <time>{item.as_of || t('brief.coverage.unknownDate')}</time><span className={`brief-freshness__state brief-freshness__state--${item.state}`}>{t(`brief.coverage.${item.state}`)}</span></li>)}
        </ul><p className="brief-coverage__note">{t('brief.coverage.currentNote')}</p></div>}
      </details>}
    </section>
  )
}
